import { GENERIC_ERROR_MESSAGE } from '@shared/i18n';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { staticFunctions } from '@shared/contexts';
import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import isAiEditDocumentEligible from '@modules/documents/helpers/isAiEditDocumentEligible.helper';
import { isLargeDocument } from '@modules/documents/largeDocument';

import isCostlyAiDocument from '../helpers/isCostlyAiDocument.helper';
import useAiOpenVersion from '../hooks/useAiOpenVersion.hook';
import useCanUseAiEdit from '../hooks/useCanUseAiEdit.hook';
import {
  type EAiMode,
  type TAiAssistantSurface,
  EAiAssistantOriginItemType,
  type TAiContentSelection,
  type TAiAssistantOrigin,
} from '../types';

import { AI_EDIT_SELECTION_NOT_RESTORED_MESSAGE } from './constants';
import restoreAiEditSelection from './restoreAiEditSelection';
import useAiEditReview from './useAiEditReview.hook';
import useAiEditRun, { type TAiEditRunStart } from './useAiEditRun.hook';

type TAiEditPhase = 'idle' | 'requesting-edit' | 'entering-edit';

type TAiEditStage =
  | 'idle'
  | 'selection-reminder'
  | 'draft-confirm'
  | 'opening-draft'
  | 'queued'
  | 'running'
  | 'reviewing';

/** Every flag a consumer reads comes from this one row, so no two of them can disagree. */
const AI_EDIT_STAGE: Record<TAiEditStage, { isActive: boolean; isPending: boolean }> = {
  idle: { isActive: false, isPending: false },
  'selection-reminder': { isActive: true, isPending: false },
  'draft-confirm': { isActive: true, isPending: false },
  'opening-draft': { isActive: true, isPending: true },
  queued: { isActive: true, isPending: true },
  running: { isActive: true, isPending: true },
  reviewing: { isActive: true, isPending: false },
};

type TParams = {
  surface: TAiAssistantSurface;
  canEditDocument: boolean;
  origin: TAiAssistantOrigin | undefined;
};

const useAiEdit = ({ surface, canEditDocument, origin }: TParams) => {
  const documentContext = useContext(DocumentContext);
  const canUseAiEdit = useCanUseAiEdit();
  const { isDraftOpenElsewhere } = useAiOpenVersion();
  const startedQueuedEditRef = useRef<TAiEditRunStart>();

  const [phase, setPhase] = useState<TAiEditPhase>('idle');
  const [queuedEdit, setQueuedEdit] = useState<TAiEditRunStart>();
  const [submittedWithSelection, setSubmittedWithSelection] = useState(false);
  const [selectionReminderRequest, setSelectionReminderRequest] = useState<TAiEditRunStart>();
  const [draftConfirmRequest, setDraftConfirmRequest] = useState<TAiEditRunStart>();
  const review = useAiEditReview({ surface, origin });

  const isVisible = canUseAiEdit && surface === 'sidebar' && origin?.itemType === EAiAssistantOriginItemType.DOCUMENT;
  const isAvailable =
    isVisible &&
    isAiEditDocumentEligible({
      doc: documentContext?.doc,
      version: documentContext?.version,
      canEdit: canEditDocument,
      isReplica: Boolean(documentContext?.replica),
    });
  // Started from view mode, the run never uses the editor on screen: it opens the draft and the
  // queued-edit effect waits for THAT editor. Only a start in edit mode needs this one ready.
  const isEditorReady =
    documentContext?.isEdit !== true || (Boolean(documentContext?.editor) && documentContext?.isContentReady === true);
  // A part-loaded editor holds one part, so suggestions outside it could never be planned or applied.
  const isPartLoaded = isLargeDocument();
  // AI edit calls editDraft() itself, so the disabled Edit button does not stop it.
  const hasUnsupportedContent = documentContext?.hasUnsupportedContent === true;
  const canUse = isAvailable && isEditorReady && !isPartLoaded && !hasUnsupportedContent;
  // Looking at a completed version while a draft exists: the suggestions would land in the draft,
  // not in what the user is reading, so the tooltip sends them there instead of just refusing.
  const disabledReason = !isVisible
    ? undefined
    : !isAvailable
      ? isDraftOpenElsewhere
        ? 'Switch to the draft to edit with AI.'
        : 'This document cannot be edited.'
      : hasUnsupportedContent
        ? GENERIC_ERROR_MESSAGE
        : isPartLoaded
          ? 'AI editing is not available with fast loading enabled.'
          : !isEditorReady
            ? 'Document content is still loading.'
            : undefined;

  const {
    isPending: isRunPending,
    progressMessage: runProgressMessage,
    budgetRefusal,
    start: startRun,
    cancel: cancelRun,
  } = useAiEditRun({ onCompleted: review.prepareCompletedRun, onFailed: review.fail });

  const run = useCallback(
    (request: TAiEditRunStart) => {
      if (!review.begin()) {
        setPhase('idle');
        return;
      }

      setPhase('idle');
      if (startRun(request)) return;

      review.fail(GENERIC_ERROR_MESSAGE);
    },
    [review.begin, review.fail, startRun]
  );

  const cancel = useCallback(() => {
    cancelRun();
    review.cancel();
    setQueuedEdit(undefined);
    setSelectionReminderRequest(undefined);
    setDraftConfirmRequest(undefined);
    setPhase('idle');
  }, [cancelRun, review.cancel]);

  const stage: TAiEditStage = isRunPending
    ? 'running'
    : review.isActive
      ? 'reviewing'
      : phase !== 'idle'
        ? 'opening-draft'
        : queuedEdit
          ? 'queued'
          : selectionReminderRequest
            ? 'selection-reminder'
            : draftConfirmRequest
              ? 'draft-confirm'
              : 'idle';

  const { isActive, isPending } = AI_EDIT_STAGE[stage];
  // One message from the click to the first server update: opening the draft and saving it are
  // a blink on a small document, and a message that short only flickers.
  const progressMessage = runProgressMessage;
  const hasSelectionContext = Boolean(queuedEdit?.selection || (isPending && submittedWithSelection));

  useEffect(() => {
    if (phase === 'requesting-edit') {
      if (documentContext?.requestEdit) setPhase('entering-edit');
      return;
    }
    // Returning to view mode settles a refused or failed draft request before an AI run exists.
    if (phase === 'entering-edit' && !documentContext?.requestEdit && !documentContext?.isEdit) cancel();
  }, [phase, documentContext?.requestEdit, documentContext?.isEdit, cancel]);

  useEffect(() => {
    // Planning positions are valid only after the exact editable draft has replaced the view-mode document.
    if (
      !queuedEdit ||
      !documentContext?.isEdit ||
      !documentContext.isContentReady ||
      documentContext.versionId !== 'draft'
    ) {
      return;
    }
    // Setting the restored editor selection can rerender before React commits setQueuedEdit.
    // Claim this queued edit first so restoration and startup happen exactly once.
    if (startedQueuedEditRef.current === queuedEdit) return;
    startedQueuedEditRef.current = queuedEdit;
    if (!restoreAiEditSelection(documentContext.editor, queuedEdit.selection)) {
      staticFunctions.show('warning', AI_EDIT_SELECTION_NOT_RESTORED_MESSAGE);
    }
    setQueuedEdit(undefined);
    run(queuedEdit);
  }, [queuedEdit, documentContext?.isEdit, documentContext?.isContentReady, documentContext?.versionId, run]);

  const startQueued = (request: TAiEditRunStart) => {
    setQueuedEdit(request);
    if (!documentContext?.isEdit) {
      setPhase(documentContext?.requestEdit ? 'entering-edit' : 'requesting-edit');
      if (!documentContext?.requestEdit) documentContext?.editDraft();
    }
  };

  // Edit works in a draft: with none yet, ask before creating it. A draft that exists but is not
  // open in edit mode needs no question - editDraft only switches to it.
  const startOrConfirmDraft = (request: TAiEditRunStart) => {
    if (!documentContext?.isEdit && !documentContext?.doc?.draft && !documentContext?.requestEdit) {
      setDraftConfirmRequest(request);
      return;
    }
    startQueued(request);
  };

  const queue = (prompt: string, selection: TAiContentSelection | undefined, aiMode: EAiMode) => {
    if (!canUse || !documentContext) return;
    review.clearResult();
    setSubmittedWithSelection(Boolean(selection));
    // Asked before any draft opening starts, so the reminder never interrupts a progress state.
    if (!selection && isCostlyAiDocument(documentContext.content) && documentContext.consumeAiEditSelectionReminder()) {
      setSelectionReminderRequest({ aiMode, prompt });
      return;
    }
    startOrConfirmDraft({ aiMode, prompt, selection });
  };

  const submit = (prompt: string, selection: TAiContentSelection | undefined, aiMode: EAiMode) => {
    queue(prompt, selection, aiMode);
  };

  const continueWithoutSelection = () => {
    if (!selectionReminderRequest) return;
    setSelectionReminderRequest(undefined);
    startOrConfirmDraft(selectionReminderRequest);
  };

  const confirmCreateDraft = () => {
    if (!draftConfirmRequest) return;
    setDraftConfirmRequest(undefined);
    startQueued(draftConfirmRequest);
  };

  const cancelCreateDraft = () => setDraftConfirmRequest(undefined);

  const selectTextFirst = () => {
    setSelectionReminderRequest(undefined);
    documentContext?.editor.commands.focus();
  };

  return {
    isVisible,
    isAvailable,
    canUse,
    disabledReason,
    isActive,
    isPending,
    hasSelectionContext,
    isBusy: review.isBusy,
    canRevert: review.canRevert,
    progressMessage,
    budgetRefusal,
    result: review.result,
    followUpPrompt: review.followUpPrompt,
    statusMessage: review.statusMessage,
    changeIndex: review.changeIndex,
    submittedFeedback: review.submittedFeedback,
    setSubmittedFeedback: review.setSubmittedFeedback,
    isSelectionReminderOpen: Boolean(selectionReminderRequest),
    setSelectionReminderOpen: (open: boolean) => !open && setSelectionReminderRequest(undefined),
    draftConfirmRequest,
    confirmCreateDraft,
    cancelCreateDraft,
    submit,
    cancel,
    accept: review.accept,
    reject: review.reject,
    revert: review.revert,
    navigate: review.navigate,
    continueWithoutSelection,
    selectTextFirst,
    clearResult: review.clearResult,
  };
};

export default useAiEdit;
